import { mcGetItem, mcSetItem } from './mcCloudSync';
import { recordActivity } from './mcActivityEvents';

export const MC_ORG_META_KEY = 'mc-org-meta';
export const MC_SITES_KEY = 'mc-sites';
export const MC_SITE_EVENT = 'mc-site-change';
export const MC_SITE_RESPONSIBILITY_BACKUP_KEY = 'mc-site-responsibility-backup';
export const MC_CSV_SITE_RESOLUTION_KEY = 'mc-csv-site-resolution';

function safeParse(raw, fallback) {
  try { return raw ? JSON.parse(raw) : fallback; } catch { return fallback; }
}

export function normalizeSiteId(value = '') {
  const raw = String(value || '').replace(/^inventory_/i, '').replace(/_Store$/i, '').trim();
  const id = raw.toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return id || 'default-site';
}

export function labelFromSiteValue(value = '') {
  const raw = String(value || '').replace(/^inventory_/i, '').replace(/_Store$/i, '').trim();
  if (!raw || raw.toUpperCase() === 'ALL') return 'All Sites';
  return raw.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim().replace(/\b\w/g, (m) => m.toUpperCase());
}

export function inventoryHeaderFromSiteName(name = '') {
  const compact = String(name || '').replace(/^inventory_/i, '').replace(/_Store$/i, '').trim().replace(/\s+/g, '_');
  return compact ? `inventory_${compact}` : '';
}

export function isInventoryHeader(value = '') {
  return /^inventory_/i.test(String(value || '').trim());
}

export function detectSiteHeaders(headers = []) {
  return Array.from(new Set((headers || []).filter(isInventoryHeader)));
}

export function loadOrgMeta() {
  return safeParse(mcGetItem(MC_ORG_META_KEY), { name: 'Morpheus Organisation', billingMode: 'per_site' });
}

export function saveOrgMeta(meta = {}) {
  const next = { ...loadOrgMeta(), ...meta, updatedAt: new Date().toISOString() };
  mcSetItem(MC_ORG_META_KEY, JSON.stringify(next));
  return next;
}

function trustedImportHeadersFromRecord(record = {}) {
  if (!record || typeof record !== 'object') return [];
  const values = [];
  if (Array.isArray(record.parsedHeaders)) values.push(...detectSiteHeaders(record.parsedHeaders));
  [
    record.inventoryKey,
    record.csvKey,
    record.mapping?.systemSoh,
  ].forEach((value) => {
    if (isInventoryHeader(value)) values.push(String(value).trim());
  });
  return values;
}

function recoverSitesFromOperationalCache() {
  if (typeof window === 'undefined') return [];
  const recovered = new Map();

  const addHeader = (header) => {
    const raw = String(header || '').trim();
    if (!isInventoryHeader(raw)) return;
    const site = canonicalizeSiteRecord({
      siteId: normalizeSiteId(raw),
      name: labelFromSiteValue(raw),
      storeKey: raw,
      aliases: [raw, labelFromSiteValue(raw)],
      source: 'import',
      importBacked: true,
    });
    if (!site.siteId || site.siteId === 'default-site') return;
    const previous = recovered.get(site.siteId);
    recovered.set(site.siteId, previous ? canonicalizeSiteRecord({
      ...previous,
      ...site,
      aliases: [...(previous.aliases || []), ...(site.aliases || [])],
      source: 'import',
      importBacked: true,
    }) : site);
  };

  const visit = (value, depth = 0) => {
    if (depth > 5 || value == null) return;
    if (Array.isArray(value)) {
      value.forEach((item) => visit(item, depth + 1));
      return;
    }
    if (typeof value !== 'object') return;

    trustedImportHeadersFromRecord(value).forEach(addHeader);
    ['records', 'items', 'data', 'imports', 'files'].forEach((key) => {
      if (value[key] !== undefined) visit(value[key], depth + 1);
    });
  };

  const candidateKeys = new Set([
    'mc-logic-pass',
    'mc-import-active',
  ]);

  const collectKeys = (storage) => {
    if (!storage) return;
    try {
      for (let index = 0; index < storage.length; index += 1) {
        const key = storage.key(index) || '';
        if (/^mc-(?:logic-pass|engine|import-active)(?::|_|-)/i.test(key)) candidateKeys.add(key);
      }
    } catch (_) {}
  };

  collectKeys(window.localStorage);
  collectKeys(window.sessionStorage);

  candidateKeys.forEach((key) => {
    const parsed = safeParse(mcGetItem(key), null);
    if (parsed !== null) visit(parsed);
  });

  return Array.from(recovered.values());
}

export function isImportBackedSite(site = {}, headers = []) {
  const detected = detectSiteHeaders(headers || []);
  const trustedIds = new Set(detected.map(normalizeSiteId));
  const siteId = site.siteId || normalizeSiteId(site.storeKey || site.name);
  if (trustedIds.has(siteId)) return true;
  if (site.importBacked === true || site.source === 'import') return true;
  return false;
}


function loadSiteResponsibilityBackup() {
  const parsed = safeParse(mcGetItem(MC_SITE_RESPONSIBILITY_BACKUP_KEY), {});
  return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
}

function applySiteResponsibilityBackup(site = {}, backup = {}) {
  const saved = backup?.[site.siteId] || {};
  const assigned = (key) => Object.prototype.hasOwnProperty.call(saved, key) ? saved[key] || '' : site[key] || '';
  return {
    ...site,
    siteManagerPersonId: assigned('siteManagerPersonId'),
    operationsManagerPersonId: assigned('operationsManagerPersonId'),
    ownerPersonId: assigned('ownerPersonId'),
  };
}

function saveSiteResponsibilityBackup(sites = []) {
  const next = {};
  (sites || []).forEach((site) => {
    if (!site?.siteId) return;
    next[site.siteId] = {
      siteManagerPersonId: site.siteManagerPersonId || '',
      operationsManagerPersonId: site.operationsManagerPersonId || '',
      ownerPersonId: site.ownerPersonId || '',
      updatedAt: new Date().toISOString(),
    };
  });
  mcSetItem(MC_SITE_RESPONSIBILITY_BACKUP_KEY, JSON.stringify(next));
  return next;
}

function preserveSiteResponsibilities(sites = [], backup = {}) {
  return (sites || []).map((site) => applySiteResponsibilityBackup(site, backup));
}
function readRowSiteResolutionManifest() {
  const candidates = [];
  const durable = safeParse(mcGetItem(MC_CSV_SITE_RESOLUTION_KEY), null);
  if (durable) candidates.push(durable);

  // Mapping also stores the confirmed resolution on the active engine/import
  // profile. Older orgs may have this canonical source even when the small
  // durable resolution document was never written or was overwritten by a
  // stale browser cache.
  ['mc-logic-pass', 'mc-import-active'].forEach((key) => {
    const record = safeParse(mcGetItem(key), null);
    if (!record || typeof record !== 'object') return;
    const profile = record.importSourceProfile || record.sourceProfile || {};
    const resolution = profile.siteResolution || record.siteResolution;
    if (resolution) candidates.push({
      ...resolution,
      sourceLayout: profile.layout || record.sourceLayout || 'row_site',
      sourceHeader: resolution.sourceHeader || profile.rowSiteHeader || '',
    });
  });

  for (const resolution of candidates) {
    const layout = String(resolution?.sourceLayout || resolution?.layout || '').toLowerCase();
    const values = Array.isArray(resolution?.values) ? resolution.values : [];
    if (!['row_site', 'row-based sites', 'row_based_sites'].includes(layout) || !values.length) continue;

    const mapped = values
      .filter((item) => item?.action === 'map' && item?.siteId)
      .map((item) => ({
        sourceValue: String(item.sourceValue || item.value || item.label || '').trim(),
        siteId: normalizeSiteId(item.siteId),
      }))
      .filter((item) => item.siteId && item.siteId !== 'default-site');

    if (mapped.length) return mapped;
  }
  return null;
}

function buildAuthoritativeRowSites(storedSites = [], manifest = []) {
  const storedById = new Map(
    (storedSites || []).filter(Boolean).map((site) => {
      const canonical = canonicalizeSiteRecord(site);
      return [normalizeSiteId(canonical.siteId), canonical];
    })
  );

  return manifest.map(({ sourceValue, siteId }) => {
    const existing = storedById.get(normalizeSiteId(siteId));
    const fallbackName = existing?.name || labelFromSiteValue(sourceValue || siteId);
    return canonicalizeSiteRecord({
      ...(existing || {}),
      siteId,
      name: fallbackName,
      storeKey: existing?.storeKey || inventoryHeaderFromSiteName(fallbackName),
      aliases: Array.from(new Set([
        ...(existing?.aliases || []),
        sourceValue,
        fallbackName,
        inventoryHeaderFromSiteName(fallbackName),
      ].filter(Boolean))),
      source: existing?.source || 'mapping',
      importBacked: true,
    });
  });
}

export function loadSites() {
  const parsed = safeParse(mcGetItem(MC_SITES_KEY), []);
  const storedSites = Array.isArray(parsed) ? parsed : [];
  const manifest = readRowSiteResolutionManifest();
  const responsibilityBackup = loadSiteResponsibilityBackup();

  // A confirmed row-site resolution is the organisation's authoritative site
  // manifest. Do not merge operational-cache recovery in this mode: those
  // browser caches can contain another organisation's historical inventory and
  // were the source of the 21-site cross-org leak.
  if (manifest) {
    // The row-site manifest describes sites present in the current import; it is
    // not the complete organisation registry. Keep every org-scoped stored site
    // AND recover site identities from the current organisation's operational
    // inventory cache before overlaying the manifest. mcCloudSync clears MC cache
    // on org switches, so these recovered inventory headers are tenant-safe and
    // prevent a partial row-site import from collapsing a previously complete
    // 10-site registry after refresh.
    const recoveredSites = recoverSitesFromOperationalCache();
    const byId = new Map();
    [...storedSites, ...recoveredSites].filter(Boolean).forEach((site) => {
      const canonical = canonicalizeSiteRecord(site);
      if (!canonical.siteId) return;
      const key = normalizeSiteId(canonical.siteId);
      const previous = byId.get(key);
      byId.set(key, previous ? canonicalizeSiteRecord({
        ...canonical,
        ...previous,
        aliases: [...(canonical.aliases || []), ...(previous.aliases || [])],
        active: previous.active !== false,
        billingEnabled: previous.billingEnabled !== false,
      }) : canonical);
    });
    buildAuthoritativeRowSites(storedSites, manifest).forEach((site) => {
      const canonical = canonicalizeSiteRecord(site);
      const key = normalizeSiteId(canonical.siteId);
      const previous = byId.get(key);
      byId.set(key, previous ? canonicalizeSiteRecord({
        ...previous,
        ...canonical,
        aliases: [...(previous.aliases || []), ...(canonical.aliases || [])],
        active: previous.active !== false,
        billingEnabled: previous.billingEnabled !== false,
      }) : canonical);
    });
    return Array.from(byId.values())
      .map((site) => applySiteResponsibilityBackup(site, responsibilityBackup))
      .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')));
  }

  const recoveredSites = recoverSitesFromOperationalCache();
  const byId = new Map();
  [...storedSites, ...recoveredSites].filter(Boolean).forEach((site) => {
    const canonical = canonicalizeSiteRecord(site);
    const previous = byId.get(canonical.siteId);
    byId.set(canonical.siteId, previous ? canonicalizeSiteRecord({
      ...canonical,
      ...previous,
      aliases: [...(previous.aliases || []), ...(canonical.aliases || [])],
      active: previous.active !== false,
      billingEnabled: previous.billingEnabled !== false,
    }) : canonical);
  });

  return Array.from(byId.values())
    .map((site) => applySiteResponsibilityBackup(site, responsibilityBackup))
    .sort((a, b) => {
      const aName = String(a?.name || a?.storeKey || '').toLowerCase();
      const bName = String(b?.name || b?.storeKey || '').toLowerCase();
      return aName.localeCompare(bName);
    });
}

export function saveSites(sites = [], options = {}) {
  const stamp = new Date().toISOString();
  const responsibilityBackup = loadSiteResponsibilityBackup();
  const rawDeduped = Array.from(new Map((sites || []).filter(Boolean).map((site) => [site.siteId || normalizeSiteId(site.name || site.storeKey), {
    ...site,
    siteId: site.siteId || normalizeSiteId(site.name || site.storeKey),
    name: site.name || labelFromSiteValue(site.storeKey || site.siteId),
    active: site.active !== false,
    billingEnabled: site.billingEnabled !== false,
    aliases: Array.from(new Set([...(site.aliases || []), site.storeKey].filter(Boolean))),
    createdAt: site.createdAt || stamp,
    updatedAt: site.updatedAt || stamp,
  }])).values());

  // Site discovery/import reconciliation calls saveSites too. Those background
  // saves must NEVER turn an omitted/blank responsibility field into a durable
  // clear. The dedicated responsibility ledger is authoritative and is merged
  // back into the registry on every non-admin save. Only the Sites page passes
  // replaceResponsibilityBackup=true for an intentional assignment/clear.
  const replaceResponsibilityBackup = options?.replaceResponsibilityBackup === true;
  const deduped = replaceResponsibilityBackup
    ? rawDeduped
    : preserveSiteResponsibilities(rawDeduped, responsibilityBackup);

  if (replaceResponsibilityBackup) saveSiteResponsibilityBackup(deduped);
  const previousRaw = mcGetItem(MC_SITES_KEY) || '[]';
  const nextRaw = JSON.stringify(deduped);
  if (previousRaw !== nextRaw) {
    const previous = safeParse(previousRaw, []);
    mcSetItem(MC_SITES_KEY, nextRaw);
    const beforeById = new Map((previous || []).map((site) => [site?.siteId, site]));
    deduped.forEach((site) => {
      const before = beforeById.get(site?.siteId);
      if (!before) recordActivity({category:'People',action:'Site created',title:`Site created · ${site?.name || 'Site'}`,site:site?.name || 'All Sites',targetId:site?.siteId,targetName:site?.name});
      else {
        const keys=['name','storeKey','active','siteManagerPersonId','operationsManagerPersonId','ownerPersonId'];
        const changed=keys.filter((key)=>JSON.stringify(before?.[key]??'')!==JSON.stringify(site?.[key]??''));
        if (changed.length) recordActivity({category:'People',action:'Site responsibility changed',title:`Site updated · ${site?.name || 'Site'}`,site:site?.name || 'All Sites',targetId:site?.siteId,targetName:site?.name,metadata:{changedFields:changed}});
      }
      beforeById.delete(site?.siteId);
    });
    beforeById.forEach((site)=>recordActivity({category:'People',action:'Site removed',title:`Site removed · ${site?.name || 'Site'}`,site:site?.name || 'All Sites',targetId:site?.siteId,targetName:site?.name,severity:'attention'}));
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(MC_SITE_EVENT, { detail: deduped }));
  }
  return deduped;
}

export function ensureSitesFromHeaders(headers = []) {
  const current = loadSites();
  const detected = detectSiteHeaders(headers);
  if (!detected.length) return current;

  let changed = false;
  const stamp = new Date().toISOString();
  const byId = new Map(current.map((site) => [site.siteId || normalizeSiteId(site.name || site.storeKey), site]));

  detected.forEach((header) => {
    const siteId = normalizeSiteId(header);
    const existing = byId.get(siteId) || {};
    const aliases = Array.from(new Set([...(existing.aliases || []), header, labelFromSiteValue(header), inventoryHeaderFromSiteName(labelFromSiteValue(header))].filter(Boolean)));
    const next = {
      ...existing,
      siteId,
      name: existing.name || labelFromSiteValue(header),
      storeKey: existing.storeKey || header,
      active: existing.active !== false,
      billingEnabled: existing.billingEnabled !== false,
      aliases,
      source: 'import',
      importBacked: true,
      createdAt: existing.createdAt || stamp,
      updatedAt: existing.updatedAt || stamp,
    };

    const before = JSON.stringify(existing);
    const after = JSON.stringify(next);
    if (!existing.siteId || before !== after) changed = true;
    byId.set(siteId, next);
  });

  const nextSites = Array.from(byId.values());
  return changed ? saveSites(nextSites) : nextSites;
}

function cleanCompare(value = '') {
  return labelFromSiteValue(value).toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function resolveSite(value = '', headers = []) {
  const sites = headers?.length ? ensureSitesFromHeaders(headers) : loadSites();
  const raw = String(value || '').trim();
  if (!raw || raw.toUpperCase() === 'ALL') return null;
  const target = cleanCompare(raw);
  const found = sites.find((site) => {
    const candidates = [site.siteId, site.name, site.storeKey, ...(site.aliases || [])];
    return candidates.some((candidate) => cleanCompare(candidate) === target || String(candidate || '').trim().toLowerCase() === raw.toLowerCase());
  });
  if (found) return found;

  // Pure fallback only. Do not save here because resolveSite is used inside render-time filters.
  const name = labelFromSiteValue(raw);
  const storeKey = isInventoryHeader(raw) ? raw : inventoryHeaderFromSiteName(name);
  return {
    siteId: normalizeSiteId(raw),
    name,
    storeKey,
    active: true,
    billingEnabled: true,
    aliases: Array.from(new Set([raw, storeKey, name].filter(Boolean))),
  };
}

export function getSiteOptions(headers = []) {
  const detected = detectSiteHeaders(headers || []);
  const sites = detected.length ? ensureSitesFromHeaders(headers || []) : loadSites();
  return sites.filter((site) => site.active !== false).map((site) => site.storeKey || inventoryHeaderFromSiteName(site.name));
}

export function sameSite(a = {}, b = {}) {
  const siteA = a.siteId || resolveSite(a.storeKey || a.store || a.storeName || a.mapping?.systemSoh)?.siteId || '';
  const siteB = b.siteId || resolveSite(b.storeKey || b.store || b.storeName || b.mapping?.systemSoh)?.siteId || '';
  return !!siteA && !!siteB && siteA === siteB;
}

export function siteIdForValue(value = '') {
  const raw = String(value || '').trim();
  if (!raw || raw.toUpperCase() === 'ALL') return 'ALL';
  const site = resolveSite(raw);
  return site?.siteId || normalizeSiteId(raw);
}

export function canonicalizeSiteRecord(site = {}) {
  const rawValue = site.siteId || site.storeKey || site.inventoryKey || site.csvKey || site.name || '';
  const name = site.name || labelFromSiteValue(rawValue);
  const storeKey = site.storeKey || site.inventoryKey || site.csvKey || (isInventoryHeader(rawValue) ? rawValue : inventoryHeaderFromSiteName(name));
  const siteId = site.siteId || normalizeSiteId(storeKey || name || rawValue);
  const aliases = Array.from(new Set([
    ...(site.aliases || []),
    site.alias,
    site.storeKey,
    site.inventoryKey,
    site.csvKey,
    storeKey,
    name,
    rawValue,
  ].filter(Boolean)));

  return {
    ...site,
    siteId,
    name,
    storeKey,
    active: site.active !== false,
    billingEnabled: site.billingEnabled !== false,
    aliases,
  };
}

export function canonicalSiteForValue(value = '') {
  return canonicalizeSiteRecord(resolveSite(value));
}

export function saveSiteAlias(siteIdOrValue = '', alias = '') {
  const siteId = siteIdForValue(siteIdOrValue);
  const cleanAlias = String(alias || '').trim();
  if (!siteId || siteId === 'ALL' || !cleanAlias) return loadSites();

  const sites = loadSites();
  const index = sites.findIndex((site) => (site.siteId || normalizeSiteId(site.storeKey || site.name)) === siteId);
  const base = index >= 0 ? sites[index] : resolveSite(siteIdOrValue);
  const nextSite = canonicalizeSiteRecord({
    ...base,
    storeKey: isInventoryHeader(cleanAlias) ? cleanAlias : base.storeKey,
    aliases: Array.from(new Set([...(base.aliases || []), cleanAlias, labelFromSiteValue(cleanAlias)].filter(Boolean))),
  });

  const nextSites = index >= 0 ? sites.map((site, i) => (i === index ? nextSite : site)) : [...sites, nextSite];
  return saveSites(nextSites);
}

export function saveCsvHeaderSiteMappings(mappings = {}) {
  const entries = Array.isArray(mappings)
    ? mappings.map((item) => [item.header || item.csvHeader || item.storeKey, item.siteId || item.site || item.value])
    : Object.entries(mappings || {});

  let sites = loadSites();
  entries.forEach(([header, target]) => {
    const cleanHeader = String(header || '').trim();
    if (!cleanHeader) return;
    const targetValue = target || cleanHeader;
    const targetId = siteIdForValue(targetValue);
    if (!targetId || targetId === 'ALL') return;

    const existingIndex = sites.findIndex((site) => (site.siteId || normalizeSiteId(site.storeKey || site.name)) === targetId);
    const existing = existingIndex >= 0 ? sites[existingIndex] : resolveSite(targetValue);
    const nextSite = canonicalizeSiteRecord({
      ...existing,
      storeKey: isInventoryHeader(cleanHeader) ? cleanHeader : existing.storeKey,
      aliases: Array.from(new Set([...(existing.aliases || []), cleanHeader, labelFromSiteValue(cleanHeader)].filter(Boolean))),
    });
    if (existingIndex >= 0) sites[existingIndex] = nextSite;
    else sites.push(nextSite);
  });

  return saveSites(sites);
}

export function activeBillingSiteCount() {
  return loadSites().filter((site) => site.active !== false && site.billingEnabled !== false).length;
}
